import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { triggerChat } from '@/utils/chatTrigger';

const Comparison = () => {
  const rows = [
    { feature: "Feels human", form: false, bot: false, live: true },
    { feature: "Answers real questions", form: false, bot: "Limited", live: true },
    { feature: "Builds instant trust", form: false, bot: false, live: true },
    { feature: "No typing required", form: false, bot: false, live: true },
    { feature: "Books the appointment live", form: false, bot: "Partial", live: true },
    { feature: "Converts hesitant visitors", form: false, bot: false, live: true }
  ];

  const renderIcon = (value, isMobile = false) => {
    if (value === true) return <SafeIcon icon={FiIcons.FiCheck} className={`${isMobile ? 'text-xl' : 'text-2xl'} text-green-400 mx-auto`} />;
    if (value === false) return <SafeIcon icon={FiIcons.FiX} className={`${isMobile ? 'text-xl' : 'text-2xl'} text-red-500/50 mx-auto`} />;
    return <span className={`text-gray-400 font-medium italic mx-auto block text-center ${isMobile ? 'text-xs' : 'text-base'}`}>({value})</span>;
  };

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-anton uppercase tracking-wide text-white">
            Form vs. Chatbot vs. <span className="text-gold">AgentOnDemand</span>
          </h2>
        </motion.div>

        {/* --- DESKTOP VIEW (Visible on lg and up) --- */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="hidden lg:block overflow-x-auto pb-8"
        >
          <div className="min-w-[700px] w-full bg-navy-lighter rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
            <div className="grid grid-cols-4 bg-navy/50 border-b border-white/10">
              <div className="p-6"></div>
              <div className="p-6 text-center">
                <h4 className="font-anton uppercase text-xl text-gray-400">Contact Form</h4>
              </div>
              <div className="p-6 text-center">
                <h4 className="font-anton uppercase text-xl text-gray-400">AI Chatbot</h4>
              </div>
              <div className="p-6 text-center bg-gold/10 border-b-2 border-gold relative">
                <h4 className="font-anton uppercase text-xl text-gold">AgentOnDemand</h4>
              </div>
            </div>

            <div className="divide-y divide-white/5">
              {rows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-4 group hover:bg-white/[0.02] transition-colors">
                  <div className="p-6 flex items-center">
                    <span className="text-lg font-medium text-white">{row.feature}</span>
                  </div>
                  <div className="p-6 flex items-center justify-center border-l border-white/5">
                    {renderIcon(row.form)}
                  </div>
                  <div className="p-6 flex items-center justify-center border-l border-white/5">
                    {renderIcon(row.bot)}
                  </div>
                  <div className="p-6 flex items-center justify-center border-l border-white/5 bg-gold/[0.03]">
                    {renderIcon(row.live)}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-4 bg-navy/50 border-t border-white/10">
              <div className="col-span-3"></div>
              <div className="p-6 text-center bg-gold/[0.03]">
                <button 
                  onClick={triggerChat} 
                  className="inline-block px-6 py-2 bg-gold text-navy font-bold uppercase rounded hover:bg-gold-hover transition-colors text-sm w-full"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* --- MOBILE VIEW (Visible below lg) --- */}
        <div className="lg:hidden space-y-4">
          {rows.map((row, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="bg-navy-lighter p-5 rounded-2xl border border-white/10 shadow-lg"
            >
              <h4 className="text-white font-anton uppercase tracking-wider text-lg mb-4 pb-3 border-b border-white/5">
                {row.feature}
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col items-center">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter mb-2">Form</span>
                  <div className="h-8 flex items-center">{renderIcon(row.form, true)}</div>
                </div>
                <div className="flex flex-col items-center border-x border-white/5 px-2">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter mb-2">AI Bot</span>
                  <div className="h-8 flex items-center">{renderIcon(row.bot, true)}</div>
                </div>
                <div className="flex flex-col items-center bg-gold/5 py-2 px-1 rounded-lg border border-gold/20">
                  <span className="text-[10px] font-bold text-gold uppercase tracking-tighter mb-2">Agent</span>
                  <div className="h-8 flex items-center">{renderIcon(row.live, true)}</div>
                </div>
              </div>
            </motion.div>
          ))}
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-6"
          >
            <button 
              onClick={triggerChat} 
              className="w-full py-4 bg-gold text-navy font-bold uppercase rounded-xl hover:bg-gold-hover transition-all shadow-[0_10px_20px_rgba(245,166,35,0.2)] active:scale-95"
            >
              Get Started Now
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;